import { API_BASE_URL } from './authConfig';

export interface UploadTicket {
  uploadUrl: string;
  blobName: string;
  expiresOn: string;
}

/**
 * Request a short-lived upload SAS from the Function API.
 * The API returns a URL that can be PUT to directly (bypassing the API).
 */
export async function requestUploadSas(
  accessToken: string,
  packageId: string,
  fileName: string,
  contentType: string,
  overwrite = false,
): Promise<UploadTicket> {
  const resp = await fetch(
    `${API_BASE_URL}/api/packages/${encodeURIComponent(packageId)}/uploads`,
    {
      method: 'POST',
      headers: {
        // Custom header — SWA edge middleware rewrites `Authorization` for its
        // own client-principal token, so we bypass that entirely.
        'X-Access-Token': accessToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fileName, contentType, overwrite }),
    },
  );

  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`API ${resp.status}: ${text || resp.statusText}`);
  }
  return (await resp.json()) as UploadTicket;
}

/**
 * PUT the file bytes directly to Azure Blob Storage using the SAS URL.
 * Returns the ETag of the created blob on success.
 */
export async function uploadFileToBlob(
  ticket: UploadTicket,
  file: File,
  onProgress?: (loaded: number, total: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', ticket.uploadUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    if (file.type) {
      xhr.setRequestHeader('x-ms-blob-content-type', file.type);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.getResponseHeader('ETag') ?? '');
      } else {
        reject(new Error(`Storage PUT ${xhr.status}: ${xhr.responseText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error uploading to Storage'));
    xhr.send(file);
  });
}
